import React, { useEffect } from 'react';
import { Card } from '../components/ui/card';
import Header from '../components/Header';
import { useLanguage } from '../context/LanguageContext';
import { CreditCard, Calendar, Clock, Shield, Home, AlertCircle, CheckCircle, XCircle, Banknote } from 'lucide-react';

const Bedingungen = () => {
  const { language } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const texts = {
    de: {
      title: 'Buchungsbedingungen',
      subtitle: 'Villa Thailand – Ihre Ferienunterkunft in Pranburi',
      
      // Payment
      paymentTitle: 'Zahlungsbedingungen',
      deposit: 'Anzahlung',
      depositText: '30% des Gesamtpreises bei Buchungsbestätigung',
      balance: 'Restzahlung',
      balanceText: '70% bei Anreise',
      methods: 'Zahlungsmethoden',
      methodsList: ['Banküberweisung', 'PayPal', 'Kreditkarte', 'Barzahlung vor Ort'],
      
      // Cancellation
      cancelTitle: 'Stornierungsbedingungen',
      cancelFree: 'Kostenlose Stornierung',
      cancelFreeText: 'Bis 30 Tage vor Anreise: Volle Rückerstattung der Anzahlung',
      cancel30: '30–14 Tage vor Anreise',
      cancel30Text: '50% der Gesamtkosten werden einbehalten',
      cancel14: 'Weniger als 14 Tage vor Anreise',
      cancel14Text: '100% der Gesamtkosten werden einbehalten',
      
      // Deposit/Kaution
      kautionTitle: 'Kaution',
      kautionAmount: 'Höhe der Kaution',
      kautionAmountText: '500 € / 500 USD / 20.000 THB',
      kautionReturn: 'Rückzahlung',
      kautionReturnCash: 'Bei Barzahlung: Rückgabe bei der Übergabe vor Abreise',
      kautionReturnTransfer: 'Bei Überweisung / PayPal / Kreditkarte: Sofortige Rückerstattung bei Abreise',
      kautionNote: 'Die Kaution dient zur Absicherung gegen eventuelle Schäden.',
      
      // Check-in/out
      checkTitle: 'Check-in & Check-out',
      checkIn: 'Check-in',
      checkInTime: 'Ab 12:00 Uhr',
      checkOut: 'Check-out',
      checkOutTime: 'Bis 12:00 Uhr',
      checkNote: 'Frühere oder spätere Zeiten auf Anfrage möglich.',
      
      // House Rules
      rulesTitle: 'Hausregeln',
      maxGuests: 'Maximale Gästezahl',
      maxGuestsText: '6 Personen',
      smoking: 'Rauchen',
      smokingText: 'Nur auf der Terrasse gestattet',
      pets: 'Haustiere',
      petsText: 'Nicht erlaubt',
      parties: 'Partys & Veranstaltungen',
      partiesText: 'Nicht erlaubt',
      
      // Liability
      liabilityTitle: 'Haftung & Schäden',
      liabilityText: 'Der Mieter haftet für alle während des Aufenthalts verursachten Schäden an der Unterkunft und deren Einrichtung.',
      cleaningTitle: 'Endreinigung',
      cleaningText: 'Im Mietpreis enthalten – keine zusätzlichen Kosten.',
      
      // Footer note
      footerNote: 'Mit der Buchung akzeptieren Sie diese Bedingungen.',
      lastUpdate: 'Stand: Januar 2026'
    },
    en: {
      title: 'Booking Terms',
      subtitle: 'Villa Thailand – Your Holiday Home in Pranburi',
      
      // Payment
      paymentTitle: 'Payment Terms',
      deposit: 'Deposit',
      depositText: '30% of total price upon booking confirmation',
      balance: 'Balance',
      balanceText: '70% upon arrival',
      methods: 'Payment Methods',
      methodsList: ['Bank Transfer', 'PayPal', 'Credit Card', 'Cash on site'],
      
      // Cancellation
      cancelTitle: 'Cancellation Policy',
      cancelFree: 'Free Cancellation',
      cancelFreeText: 'Up to 30 days before arrival: Full refund of deposit',
      cancel30: '30–14 days before arrival',
      cancel30Text: '50% of total cost will be retained',
      cancel14: 'Less than 14 days before arrival',
      cancel14Text: '100% of total cost will be retained',
      
      // Deposit/Kaution
      kautionTitle: 'Security Deposit',
      kautionAmount: 'Deposit Amount',
      kautionAmountText: '€500 / $500 USD / 20,000 THB',
      kautionReturn: 'Refund',
      kautionReturnCash: 'Cash payment: Returned at handover before departure',
      kautionReturnTransfer: 'Bank transfer / PayPal / Credit card: Immediate refund upon departure',
      kautionNote: 'The security deposit serves as protection against potential damages.',
      
      // Check-in/out
      checkTitle: 'Check-in & Check-out',
      checkIn: 'Check-in',
      checkInTime: 'From 12:00 PM',
      checkOut: 'Check-out',
      checkOutTime: 'Until 12:00 PM',
      checkNote: 'Earlier or later times available upon request.',
      
      // House Rules
      rulesTitle: 'House Rules',
      maxGuests: 'Maximum Guests',
      maxGuestsText: '6 persons',
      smoking: 'Smoking',
      smokingText: 'Only permitted on the terrace',
      pets: 'Pets',
      petsText: 'Not allowed',
      parties: 'Parties & Events',
      partiesText: 'Not allowed',
      
      // Liability
      liabilityTitle: 'Liability & Damages',
      liabilityText: 'The tenant is liable for all damages caused to the property and its furnishings during the stay.',
      cleaningTitle: 'Final Cleaning',
      cleaningText: 'Included in the rental price – no additional costs.',
      
      // Footer note
      footerNote: 'By booking, you accept these terms and conditions.',
      lastUpdate: 'Last updated: January 2026'
    },
    th: {
      title: 'เงื่อนไขการจอง',
      subtitle: 'Villa Thailand – ที่พักพักผ่อนของคุณในปราณบุรี',
      
      // Payment
      paymentTitle: 'เงื่อนไขการชำระเงิน',
      deposit: 'เงินมัดจำ',
      depositText: '30% ของราคารวมเมื่อยืนยันการจอง',
      balance: 'ยอดค้างชำระ',
      balanceText: '70% เมื่อเดินทางมาถึง',
      methods: 'วิธีการชำระเงิน',
      methodsList: ['โอนเงินผ่านธนาคาร', 'PayPal', 'บัตรเครดิต', 'เงินสดที่สถานที่'],
      
      // Cancellation
      cancelTitle: 'นโยบายการยกเลิก',
      cancelFree: 'ยกเลิกฟรี',
      cancelFreeText: 'ไม่เกิน 30 วันก่อนเข้าพัก: คืนเงินมัดจำเต็มจำนวน',
      cancel30: '30–14 วันก่อนเข้าพัก',
      cancel30Text: 'หัก 50% ของค่าใช้จ่ายทั้งหมด',
      cancel14: 'น้อยกว่า 14 วันก่อนเข้าพัก',
      cancel14Text: 'หัก 100% ของค่าใช้จ่ายทั้งหมด',
      
      // Deposit/Kaution
      kautionTitle: 'เงินประกัน',
      kautionAmount: 'จำนวนเงินประกัน',
      kautionAmountText: '€500 / $500 USD / 20,000 บาท',
      kautionReturn: 'การคืนเงิน',
      kautionReturnCash: 'ชำระเงินสด: คืนเมื่อส่งมอบก่อนออกเดินทาง',
      kautionReturnTransfer: 'โอนเงิน / PayPal / บัตรเครดิต: คืนเงินทันทีเมื่อออกเดินทาง',
      kautionNote: 'เงินประกันเพื่อป้องกันความเสียหายที่อาจเกิดขึ้น',
      
      // Check-in/out
      checkTitle: 'เช็คอิน & เช็คเอาท์',
      checkIn: 'เช็คอิน',
      checkInTime: 'ตั้งแต่ 12:00 น.',
      checkOut: 'เช็คเอาท์',
      checkOutTime: 'ภายใน 12:00 น.',
      checkNote: 'สามารถขอเวลาก่อนหรือหลังได้ตามคำขอ',
      
      // House Rules
      rulesTitle: 'กฎของบ้าน',
      maxGuests: 'จำนวนผู้เข้าพักสูงสุด',
      maxGuestsText: '6 คน',
      smoking: 'การสูบบุหรี่',
      smokingText: 'อนุญาตเฉพาะบนระเบียงเท่านั้น',
      pets: 'สัตว์เลี้ยง',
      petsText: 'ไม่อนุญาต',
      parties: 'ปาร์ตี้ & งานอีเวนต์',
      partiesText: 'ไม่อนุญาต',
      
      // Liability
      liabilityTitle: 'ความรับผิดชอบ & ความเสียหาย',
      liabilityText: 'ผู้เช่าต้องรับผิดชอบต่อความเสียหายทั้งหมดที่เกิดขึ้นกับที่พักและสิ่งอำนวยความสะดวกระหว่างการเข้าพัก',
      cleaningTitle: 'การทำความสะอาดครั้งสุดท้าย',
      cleaningText: 'รวมอยู่ในราคาเช่า – ไม่มีค่าใช้จ่ายเพิ่มเติม',
      
      // Footer note
      footerNote: 'การจองหมายถึงคุณยอมรับข้อกำหนดและเงื่อนไขเหล่านี้',
      lastUpdate: 'อัปเดตล่าสุด: มกราคม 2026'
    }
  };

  const txt = texts[language] || texts.de;

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      {/* Header */}
      <Header />

      {/* Content */}
      <main className="container mx-auto px-4 py-12 max-w-4xl pt-28">
        <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-8 text-center">{txt.title}</h1>
        
        {/* Payment Terms */}
        <Card className="p-6 border-amber-200 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-green-100 p-2 rounded-full">
              <CreditCard className="w-6 h-6 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800">{txt.paymentTitle}</h2>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
              <Banknote className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-gray-800">{txt.deposit}</p>
                <p className="text-gray-600">{txt.depositText}</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
              <Banknote className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-gray-800">{txt.balance}</p>
                <p className="text-gray-600">{txt.balanceText}</p>
              </div>
            </div>
            
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="font-semibold text-gray-800 mb-2">{txt.methods}:</p>
              <div className="flex flex-wrap gap-2">
                {txt.methodsList.map((method, index) => (
                  <span key={index} className="bg-white px-3 py-1 rounded-full text-sm border border-gray-200">
                    {method}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </Card>

        {/* Cancellation Policy */}
        <Card className="p-6 border-amber-200 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-amber-100 p-2 rounded-full">
              <Calendar className="w-6 h-6 text-amber-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800">{txt.cancelTitle}</h2>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border-l-4 border-green-500">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-green-800">{txt.cancelFree}</p>
                <p className="text-green-700">{txt.cancelFreeText}</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border-l-4 border-amber-500">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-amber-800">{txt.cancel30}</p>
                <p className="text-amber-700">{txt.cancel30Text}</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg border-l-4 border-red-500">
              <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-red-800">{txt.cancel14}</p>
                <p className="text-red-700">{txt.cancel14Text}</p>
              </div>
            </div>
          </div>
        </Card>

        {/* Security Deposit */}
        <Card className="p-6 border-amber-200 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-blue-100 p-2 rounded-full">
              <Shield className="w-6 h-6 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800">{txt.kautionTitle}</h2>
          </div>
          
          <div className="space-y-3">
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="font-semibold text-gray-800">{txt.kautionAmount}</p>
              <p className="text-2xl font-bold text-blue-600">{txt.kautionAmountText}</p>
            </div>
            
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="font-semibold text-gray-800 mb-2">{txt.kautionReturn}:</p>
              <ul className="space-y-1 text-gray-600">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-1" />
                  {txt.kautionReturnCash}
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-1" />
                  {txt.kautionReturnTransfer}
                </li>
              </ul>
            </div>
            
            <p className="text-sm text-gray-500 italic">{txt.kautionNote}</p>
          </div>
        </Card>

        {/* Check-in/out */}
        <Card className="p-6 border-amber-200 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-purple-100 p-2 rounded-full">
              <Clock className="w-6 h-6 text-purple-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800">{txt.checkTitle}</h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 bg-green-50 rounded-lg text-center">
              <p className="text-gray-600 mb-1">{txt.checkIn}</p>
              <p className="text-2xl font-bold text-green-600">{txt.checkInTime}</p>
            </div>
            <div className="p-4 bg-amber-50 rounded-lg text-center">
              <p className="text-gray-600 mb-1">{txt.checkOut}</p>
              <p className="text-2xl font-bold text-amber-600">{txt.checkOutTime}</p>
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-3 text-center">{txt.checkNote}</p>
        </Card>

        {/* House Rules */}
        <Card className="p-6 border-amber-200 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-amber-100 p-2 rounded-full">
              <Home className="w-6 h-6 text-amber-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800">{txt.rulesTitle}</h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">{txt.maxGuests}</p>
              <p className="font-semibold text-gray-800">{txt.maxGuestsText}</p>
            </div>
            <div className="p-3 bg-amber-50 rounded-lg">
              <p className="text-sm text-gray-500">{txt.smoking}</p>
              <p className="font-semibold text-amber-700">{txt.smokingText}</p>
            </div>
            <div className="p-3 bg-red-50 rounded-lg">
              <p className="text-sm text-gray-500">{txt.pets}</p>
              <p className="font-semibold text-red-700 flex items-center gap-1">
                <XCircle className="w-4 h-4" /> {txt.petsText}
              </p>
            </div>
            <div className="p-3 bg-red-50 rounded-lg">
              <p className="text-sm text-gray-500">{txt.parties}</p>
              <p className="font-semibold text-red-700 flex items-center gap-1">
                <XCircle className="w-4 h-4" /> {txt.partiesText}
              </p>
            </div>
          </div>
        </Card>

        {/* Liability & Cleaning */}
        <Card className="p-6 border-amber-200 mb-6">
          <div className="space-y-4">
            <div>
              <h3 className="font-bold text-gray-800 mb-2">{txt.liabilityTitle}</h3>
              <p className="text-gray-600">{txt.liabilityText}</p>
            </div>
            <hr className="border-gray-200" />
            <div>
              <h3 className="font-bold text-gray-800 mb-2">{txt.cleaningTitle}</h3>
              <p className="text-gray-600 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                {txt.cleaningText}
              </p>
            </div>
          </div>
        </Card>

        {/* Footer Note */}
        <div className="text-center p-6 bg-amber-100 rounded-lg">
          <p className="font-semibold text-amber-800">{txt.footerNote}</p>
          <p className="text-sm text-amber-600 mt-2">{txt.lastUpdate}</p>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-8 mt-16">
        <div className="container mx-auto px-4 text-center">
          <p className="text-gray-400">
            © 2024 Villa Thailand. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Bedingungen;
